import { useMutation, UseMutationOptions, useQuery, UseQueryOptions } from '@tanstack/react-query';

import { ENDPOINTS } from '@/api/endpoints';
import { fetchClient } from '@/api/fetch-client';
import {
  PatchReportOrganBulkError,
  PatchReportOrganBulkRequest,
  PatchReportOrganBulkResponse,
  PatchReportOrganError,
  PatchReportOrganFromUpdatedFindingsError,
  PatchReportOrganFromUpdatedFindingsRequest,
  PatchReportOrganFromUpdatedFindingsResponse,
  PatchReportOrganRequest,
  PatchReportOrganResponse,
  ReportOrgansByOrganError,
  ReportOrgansByOrganRequest,
  ReportOrgansByOrganResponse,
  ReportOrgansError,
  ReportOrgansRequest,
  ReportOrgansResponse,
} from '@/api/model/fct/report-organ';

const KEY = {
  GET_REPORT_ORGAN_BY_ORGAN: 'GET_REPORT_ORGAN_BY_ORGAN',
  GET_REPORT_ORGANS: 'GET_REPORT_ORGANS',
} as const;

const MUTATION_KEY = {
  PATCH_REPORT_ORGAN: 'PATCH_REPORT_ORGAN',
} as const;

const request = {
  getReportOrganByOrgan: async (
    { payload: { organ, reportUuid } }: ReportOrgansByOrganRequest,
    signal?: AbortSignal,
  ) => {
    const { data } = await fetchClient.FCT.get<ReportOrgansByOrganResponse>(
      `${ENDPOINTS.FCT.REPORT_ORGAN.REPORT}${reportUuid}/organ/${organ}`,
      { signal },
    );

    return data;
  },
  getReportOrgans: async (
    { payload: { reportUuid } }: ReportOrgansRequest,
    signal?: AbortSignal,
  ) => {
    const { data } = await fetchClient.FCT.get<ReportOrgansResponse>(
      `${ENDPOINTS.FCT.REPORT_ORGAN.REPORT}${reportUuid}`,
      { signal },
    );

    return data;
  },
  patchReportOrgan: async ({ options, payload }: PatchReportOrganRequest) => {
    const { data } = await fetchClient.FCT.patch<PatchReportOrganResponse>(
      `${ENDPOINTS.FCT.REPORT_ORGAN.BASE}${payload.uuid}`,
      { body: payload, options },
    );

    return data;
  },
  patchReportOrganBulk: async ({ payload }: PatchReportOrganBulkRequest) => {
    const { data } = await fetchClient.FCT.patch<PatchReportOrganBulkResponse>(
      ENDPOINTS.FCT.REPORT_ORGAN.BULK,
      { body: payload },
    );

    return data;
  },
  patchReportOrganFromUpdatedFindings: async ({
    payload,
  }: PatchReportOrganFromUpdatedFindingsRequest) => {
    const { data } = await fetchClient.FCT.patch<PatchReportOrganFromUpdatedFindingsResponse>(
      ENDPOINTS.FCT.REPORT_ORGAN.FROM_UPDATED_FINDINGS,
      { body: payload },
    );

    return data;
  },
};

const query = {
  useGetReportOrganByOrgan: (
    { payload }: ReportOrgansByOrganRequest,
    config?: Partial<UseQueryOptions<ReportOrgansByOrganResponse, ReportOrgansByOrganError>>,
  ) =>
    useQuery<ReportOrgansByOrganResponse, ReportOrgansByOrganError>({
      queryFn: ({ signal }) => request.getReportOrganByOrgan({ payload }, signal),
      queryKey: [KEY.GET_REPORT_ORGAN_BY_ORGAN, payload.reportUuid, payload.organ],
      ...config,
    }),
  useGetReportOrgans: (
    { payload }: ReportOrgansRequest,
    config?: Partial<UseQueryOptions<ReportOrgansResponse, ReportOrgansError>>,
  ) =>
    useQuery<ReportOrgansResponse, ReportOrgansError>({
      enabled: !!payload.reportUuid,
      queryFn: ({ signal }) => request.getReportOrgans({ payload }, signal),
      queryKey: [KEY.GET_REPORT_ORGANS, payload.reportUuid],
      ...config,
    }),
};

const mutation = {
  usePatchReportOrgan: (
    config?: Omit<
      UseMutationOptions<PatchReportOrganResponse, PatchReportOrganError, PatchReportOrganRequest>,
      'mutationFn' | 'mutationKey'
    >,
  ) =>
    useMutation<PatchReportOrganResponse, PatchReportOrganError, PatchReportOrganRequest>({
      ...config,
      mutationFn: request.patchReportOrgan,
      mutationKey: [MUTATION_KEY.PATCH_REPORT_ORGAN],
    }),
  usePatchReportOrganBulk: () =>
    useMutation<
      PatchReportOrganBulkResponse,
      PatchReportOrganBulkError,
      PatchReportOrganBulkRequest
    >({
      mutationFn: request.patchReportOrganBulk,
    }),
  usePatchReportOrganFromUpdatedFindings: () =>
    useMutation<
      PatchReportOrganFromUpdatedFindingsResponse,
      PatchReportOrganFromUpdatedFindingsError,
      PatchReportOrganFromUpdatedFindingsRequest
    >({
      mutationFn: request.patchReportOrganFromUpdatedFindings,
    }),
};

export const reportOrgan = {
  KEY,
  mutation,
  MUTATION_KEY,
  query,
  request,
};
